import { useEffect, useRef, useState } from "react";
import { track, trackLead } from "~/lib/analytics";
import { getAttribution } from "~/lib/attribution";
import { subscribeConsent } from "~/lib/consent";

type CampaignTrackingContext = {
  industry?: string;
  offer?: string;
  campaign_page?: string;
};

const SUBMISSION_FAILURE_CODES = ["validation_failed", "rate_limited", "save_failed"] as const;
export type FormValidationCode = "value_missing" | "type_mismatch" | "too_long" | "invalid";
export type InquiryField = "description" | "name" | "email" | "company" | "phone" | "website";

function submissionFailureCode(code?: string) {
  // A response message may contain private data. Only fixed codes reach Google.
  return SUBMISSION_FAILURE_CODES.find(allowed => allowed === code) ?? "response_rejected";
}

/** Report accepted submissions, never a submit-button tap or client validation. */
export function useLeadSubmissionTracking(form: string, result?: { ok: boolean; conversionId?: string; failure_code?: string }, service?: string, context: CampaignTrackingContext = {}) {
  const { industry, offer, campaign_page } = context;
  const fields = { form, ...(service ? { service } : {}), ...(industry ? { industry } : {}), ...(offer ? { offer } : {}), ...(campaign_page ? { campaign_page } : {}) };
  const succeeded = useRef(false);
  const started = useRef(false);
  const edited = useRef(false);
  const lastResult = useRef(result);
  useEffect(() => {
    if (!result || lastResult.current === result) return;
    lastResult.current = result;
    if (result.ok && !succeeded.current) {
      succeeded.current = true;
      trackLead({ ...fields, ...(result.conversionId ? { transaction_id: result.conversionId } : {}) });
    } else if (!result.ok) {
      track("generate_lead_failed", { ...fields, failure_code: submissionFailureCode(result.failure_code) });
    }
  }, [form, result, service, industry, offer, campaign_page]);
  return {
    onStart() {
      if (!started.current) started.current = track("form_start", fields);
    },
    onFieldEdit() {
      // A later edit can be measured after opt-in; earlier edits are not replayed.
      if (!edited.current) edited.current = track("form_input", fields);
    },
    onSubmitAttempt() {
      track("form_submit_attempt", fields);
    },
    onValidationFailed(field: InquiryField, code: FormValidationCode) {
      track("form_validation_failed", { ...fields, field, validation_code: code });
    },
  };
}

export function AttributionFields() {
  const [value, setValue] = useState("");
  useEffect(() => {
    const sync = () => {
      const fields = getAttribution();
      setValue(Object.keys(fields).length ? JSON.stringify(fields) : "");
    };
    return subscribeConsent(sync);
  }, []);
  return <input type="hidden" name="attribution" value={value} />;
}
